/* eslint-disable max-len */
/**
 * Dependencies:
 * - Custom select component
 * - Fetch cache (theme.fetchCache)
 * - Quickbuy content transformer (theme.quickBuy.convertToQuickBuyContent)
 *
 * Required translation strings:
 * - noStock
 * - noVariant
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.quickBuyContainer = this.closest('.js-quickbuy');
      this.featuredProductContainer = this.closest('.cc-featured-product');
      this.section = this.quickBuyContainer
                     || this.featuredProductContainer
                     || this.closest('.shopify-section');
      this.optionSelectors = this.querySelectorAll('.option-selector:not(.option-selector--custom)');
      this.productAvailable = this.classList.contains('variant-picker--product-available');
      this.variant = this.getVariantData();
      this.allVariants = this.getAllVariantsData();
      this.variantRows = this.getVariantRowsData();
      this.variantRequestId = 0;

      this.addListeners();
      this.updateVariantContent();
      if (!this.quickBuyContainer && !this.featuredProductContainer) {
        setTimeout(this.applySearchParams.bind(this), 0);
      }
      this.setAttribute('loaded', '');
    }

    addListeners() {
      this.boundHandleVariantChange = this.handleVariantChange.bind(this);
      this.addEventListener('change', this.boundHandleVariantChange);

      this.boundHandleLabelMouseEnter = this.handleLabelMouseEnter.bind(this);
      this.querySelectorAll('.opt-label, .custom-select__option').forEach((el) => {
        el.addEventListener('mouseenter', this.boundHandleLabelMouseEnter);
        el.addEventListener('touchstart', this.boundHandleLabelMouseEnter, { passive: true, once: true });
        el.addEventListener('mouseleave', VariantPicker.handleLabelMouseLeave);
      });
    }

    removeListeners() {
      this.removeEventListener('change', this.boundHandleVariantChange);
      this.querySelectorAll('.opt-label, .custom-select__option').forEach((el) => {
        el.removeEventListener('mouseenter', this.boundHandleLabelMouseEnter);
        el.removeEventListener('touchstart', this.boundHandleLabelMouseEnter);
        el.removeEventListener('mouseleave', VariantPicker.handleLabelMouseLeave);
      });
    }

    /**
     * Update parts of this component that require JS to display correctly.
     */
    updateVariantContent() {
      this.updateStatusClasses();
      this.updateAddToCartButton();
      this.updateSwatchLabel();
      this.updateRichRows();
      this.updateAvailability();
    }

    getProductForm() {
      return this.section.querySelector('.js-product-form');
    }

    /**
     * Handles 'mouseenter' events on option labels. Preloads links.
     * @param {object} evt - Event object.
     */
    handleLabelMouseEnter(evt) {
      const label = evt.currentTarget;
      if (label.dataset.preloaded) return; // Preload completed
      if (label.dataset.preloadTimeout) return; // Preload waiting
      if (label.matches('.opt-btn:checked + .opt-label, [aria-selected="true"]')) return; // Already selected

      // Build option array if clicked
      const input = label.htmlFor ? document.getElementById(label.htmlFor) : label;
      const optionIds = this.getSelectedOptionIds();
      const inputOptionIndex = Array.from(this.optionSelectors).indexOf(input.closest('.option-selector'));
      optionIds[inputOptionIndex] = input.dataset.valueId;

      // If all options are selected
      if (optionIds.indexOf(null) === -1) {
        // Wait to predict click-intent and preload link
        label.dataset.preloadTimeout = setTimeout(() => {
          label.dataset.preloaded = true;
          label.removeAttribute('data-preload-timeout');
          const combinedProductUrl = input.dataset.productUrl;
          const url = this.constructVariantUrl(
            combinedProductUrl,
            optionIds,
            input.dataset.variantId
          );
          theme.fetchCache.preload(url.toString());
        }, 250);
      }
    }

    /**
     * Handles 'mouseleave' events on option labels. Cancels link preload if in/out fast.
     * @param {object} evt - Event object.
     */
    static handleLabelMouseLeave(evt) {
      const label = evt.currentTarget;
      clearTimeout(label.dataset.preloadTimeout);
      label.removeAttribute('data-preload-timeout');
    }

    /**
     * Create a URL for fetching a product or variant.
     * @param {string} combinedProductUrl - URL for a combined product. Optional.
     * @param {Array} optionIds - Selected option IDs to include in the URL. Optional.
     * @param {string} variantId - Variant ID to use if not a combined product. Optional.
     * @returns {URL} Variant URL
     */
    constructVariantUrl(combinedProductUrl, optionIds, variantId) {
      let url = null;
      if (combinedProductUrl) {
        url = new URL(combinedProductUrl, window.location.origin);
        url.searchParams.set('option_values', optionIds.join(','));
      } else {
        url = new URL(this.dataset.url, window.location.origin);
      }
      const sectionIds = [this.dataset.sectionId];
      if (!this.quickBuyContainer && !this.featuredProductContainer) {
        document.querySelectorAll('.cc-variant-dependent-section [data-section-id]').forEach((e) => {
          if (e.closest('.rte')) return;
          sectionIds.push(e.dataset.sectionId);
        });
      }
      url.searchParams.set('sections', sectionIds.join(','));
      if (combinedProductUrl || !variantId || optionIds.includes(null)) {
        url.searchParams.set('option_values', optionIds.join(','));
      } else {
        url.searchParams.set('variant', variantId);
      }
      return url;
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      // A correction pass below re-triggers this same handler (via a real
      // click()/selectOption() call) for every other option it fixes up —
      // this guard makes those re-entrant calls no-ops; the original call
      // that started the correction handles everything once every input
      // reflects the corrected combination.
      if (this.correctingOptions) return;

      // Construct new variant URL and fetch it, using theme cache
      const combinedProductUrl = evt.target.dataset.productUrl || evt.detail?.productUrl;

      // If the value just picked doesn't combine with the other selected
      // option(s) into an available variant (e.g. a color the
      // currently-selected leg style doesn't come in), fix the other
      // option(s) up to the first available match right here — before
      // building any URL — instead of fetching and briefly rendering
      // Shopify's fallback for the mismatched combination ("first available
      // variant of the whole product," not of the value just picked), only
      // to have updateAvailability() correct it a moment later once that
      // fetch resolved. Combined-listing swatches (a different product,
      // absent from this.allVariants) are left alone.
      const correctedVariant = combinedProductUrl ? null : this.correctIncompatibleOptions(evt.target);

      const selectedOptionIds = this.getSelectedOptionIds();

      // Immediately update swatch label
      this.updateSwatchLabel();

      // Same reason: the rich dropdown rows carry a price and a stock line per
      // value, and on a multi-option product those belong to the combination
      // with the OTHER rows — so picking a leg style restates every colour
      // row's price. Done here rather than waiting on the section-render fetch
      // below, which never re-renders the selectors anyway.
      this.updateRichRows();

      // Switch the gallery's main image right away from client-side data
      // instead of waiting on the section-render fetch below — that fetch
      // is only rendering price/SKU/etc. HTML, and gating the image swap
      // behind it was adding a multi-second delay to every variant click.
      // Combined-listing swatches (a different product) aren't in this
      // product's own variant map, so they fall through to the fetch below
      // as before, which replaces the whole gallery once it resolves.
      if (!combinedProductUrl) {
        const resolvedVariant = correctedVariant || this.findVariantByOptions(this.getSelectedOptions());
        if (resolvedVariant) {
          this.dispatchEvent(new CustomEvent('on:variant:media-change', {
            bubbles: true,
            detail: {
              variantId: resolvedVariant.id,
              featuredMediaId: resolvedVariant.featured_media_id
            }
          }));
        }
      }

      const variantId = (correctedVariant && correctedVariant.id) || evt.target.dataset.variantId || evt.detail?.variantId;
      const url = this.constructVariantUrl(combinedProductUrl, selectedOptionIds, variantId);
      this.variantRequestId += 1;
      const currentRequestId = this.variantRequestId;

      theme.fetchCache.fetch(url.toString())
        .then((responseText) => {
          if (currentRequestId !== this.variantRequestId) return;

          const responseData = JSON.parse(responseText);

          // Update content
          Object.entries(responseData).forEach(([section, htmlStr]) => {
            const html = new DOMParser().parseFromString(htmlStr, 'text/html');
            const sectionId = `shopify-section-${section}`;
            let target = this.section;
            let newContent = html.getElementById(sectionId);

            if (this.section.id !== sectionId) {
              if (!this.quickBuyContainer) {
                target = document.getElementById(sectionId);
              } else {
                const quickBuyTemplate = html.getElementById('quick-buy-template')?.content.querySelector('.js-product');
                if (quickBuyTemplate) newContent = quickBuyTemplate;
              }
            }

            this.updateContent(target, newContent, combinedProductUrl);
          });

          // Restore focus
          if (!evt.detail?.noFocus) {
            document.querySelector(`#${evt.target.id}-button, input#${evt.target.id}`)?.focus();
          }

          // Update URL and announce changes
          const newVariantPicker = this.section.querySelector('variant-picker');
          setTimeout(() => newVariantPicker.announceChange(), 10);
        });
    }

    /**
     * Announces the current variant after a variant change.
     */
    announceChange() {
      const selectedOptionIds = this.getSelectedOptionIds();
      if (!selectedOptionIds.includes(null)) {
        this.updateUrl();
      }

      this.dispatchEvent(new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: {
          form: this.getProductForm(),
          variant: this.variant,
          selectedOptions: this.getSelectedOptions()
        }
      }));
    }

    /**
     * Replace content when the variant or product changes.
     * @param {Element} target - Target container for replacing content within.
     * @param {document} newContent - New content containing elements to use.
     * @param {boolean} productChange - Product is changing.
     */
    updateContent(target, newContent, productChange) {
      // If something's not right, do nothing
      if (!target || !newContent) return;

      // Preselection - only update certain option value attributes
      if (this.getSelectedOptions().indexOf(null) !== -1 && !productChange) {
        target.querySelectorAll('.js-option[data-value-id]').forEach((input) => {
          const newInput = newContent.querySelector(`.js-option[data-value-id="${input.dataset.valueId}"]`);
          input.className = newInput.className;
          if (newInput.dataset.variantId) {
            input.dataset.variantId = newInput.dataset.variantId;
          } else {
            delete input.dataset.variantId;
          }
          if (input.classList.contains('custom-select__option')) {
            input.innerHTML = newInput.innerHTML;
          }
        });
        this.updateStatusClasses();
        return;
      }

      let replaceSelector = '[data-dynamic-variant-content]';
      if (productChange) {
        replaceSelector += ', [data-dynamic-product-content]';
      }
      const toReplaceList = target.querySelectorAll(replaceSelector);

      toReplaceList.forEach((toReplace) => {
        const replaceWith = toReplace.dataset.dynamicVariantContent
          ? newContent.querySelector(`[data-dynamic-variant-content="${CSS.escape(toReplace.dataset.dynamicVariantContent)}"]`)
          : newContent.querySelector(`[data-dynamic-product-content="${CSS.escape(toReplace.dataset.dynamicProductContent)}"]`);
        if (!replaceWith) return;

        // Dispatch 'on:variant:before-replace-element' event before replacing element
        document.dispatchEvent(new CustomEvent('on:variant:before-replace-element', {
          bubbles: true,
          detail: {
            toReplace,
            replaceWith
          }
        }));

        // If in quick buy, content needs mutating
        if (this.quickBuyContainer && theme.quickBuy) {
          theme.quickBuy.convertToQuickBuyContent(
            replaceWith,
            this.quickBuyContainer.dataset.productUrl
          );
        }

        // Transfer basic input values
        toReplace.querySelectorAll('input[id]:not([type="hidden"]), select[id]').forEach((sourceInput) => {
          if (sourceInput.matches('.custom-select__native')) {
            const customSelectId = sourceInput.id.replace('-native', '');
            const targetInput = replaceWith.querySelector(`#${CSS.escape(customSelectId)}`);
            const option = targetInput.querySelector(`.js-option[data-value="${CSS.escape(sourceInput.value)}"]`);
            setTimeout(() => {
              targetInput.selectOption(option);
            }, 0);
          } else {
            const targetInput = replaceWith.querySelector(`#${CSS.escape(sourceInput.id)}`);
            if (!targetInput) return;
            if (sourceInput.type === 'radio' || sourceInput.type === 'checkbox') {
              targetInput.toggleAttribute('checked', sourceInput.checked);
            } else {
              targetInput.value = sourceInput.value;
            }
          }
        });

        // Replace content
        const isIdInput = replaceWith.matches('input[name="id"]');
        if (isIdInput) {
          // Update ID input - preserving listeners
          [...replaceWith.attributes].forEach(
            (attr) => toReplace.setAttribute(attr.name, attr.value)
          );
          toReplace.value = replaceWith.value;
          // Defer change event until all forms are accurate
          setTimeout(() => {
            toReplace.dispatchEvent(new Event('change', { bubbles: true }));
          }, 0);
        } else {
          // Remember toReplace becomes detached
          toReplace.replaceWith(replaceWith);
        }

        // Do not mistake input value changes for variant changes - remove listeners
        replaceWith.querySelectorAll('variant-picker').forEach((el) => el.removeListeners());

        // Set values of any unselected <custom-select> elements again
        // A fetched variant may not appear selected
        const selectedOptionIds = this.getSelectedOptionIds();
        selectedOptionIds.forEach((optionId) => {
          const toSelect = replaceWith.querySelector(`.option-selector .custom-select__option[data-value-id="${optionId}"]:not([aria-selected="true"])`);
          if (toSelect) {
            setTimeout(() => {
              toSelect.closest('custom-select').selectOption(toSelect);
            }, 0);
          }
        });

        // Update variant picker after potential changes are applied
        setTimeout(() => {
          replaceWith.querySelectorAll('variant-picker').forEach((el) => {
            el.addListeners();
            el.updateVariantContent();
          });
        }, 10);
      });
    }

    /**
     * Set status classes to help with styling.
     */
    updateStatusClasses() {
      const selectedOpts = this.getSelectedOptionIds();
      const nullCount = selectedOpts.filter((x) => x === null).length;
      const isSingleNullAtEnd = nullCount === 1 && selectedOpts[selectedOpts.length - 1] === null;
      this.classList.toggle('variant-picker--preselection', nullCount > 0);
      this.classList.toggle('variant-picker--pre-last-selection', nullCount > 0 && !isSingleNullAtEnd);
    }

    /**
     * Copies the selected value into a swatch label. (Required for pre-selection.)
     */
    updateSwatchLabel() {
      this.querySelectorAll('.option-selector:has(.js-color-text)').forEach((option) => {
        const label = option.querySelector('.js-color-text');
        const input = option.querySelector('.js-option:is(:checked, [aria-selected="true"])');
        label.textContent = input ? input.value : '';
      });
    }

    /**
     * Refreshes the price and stock line inside every rich dropdown row.
     *
     * Rows are rendered once, server-side, and the picker is never re-rendered
     * on variant change (the section-render fetch replaces the price/SKU
     * blocks, not the selectors), so on a multi-option product every row would
     * otherwise keep showing the price of whatever combination happened to be
     * selected at page load.
     */
    updateRichRows() {
      if (!this.variantRows) return;

      this.optionSelectors.forEach((selector, optionIndex) => {
        const rows = selector.querySelectorAll('.custom-select__option--rich');
        if (!rows.length) return;

        rows.forEach((row) => {
          if (row.dataset.productUrl) return;

          const variant = this.findVariantForRow(optionIndex, row.dataset.value);
          const data = variant ? this.variantRows.variants[variant.id] : null;

          const priceEl = row.querySelector('.custom-select__option-price');
          if (priceEl) {
            // innerHTML, not textContent: the price is money markup rendered by
            // the theme's own price-as-money snippet and can carry a <sup> for
            // superscript decimals. It derives from variant.price and the shop
            // money format — there is no user input anywhere in it.
            priceEl.innerHTML = data ? data.price : '';
            priceEl.hidden = !data;
          }

          const compareEl = row.querySelector('.custom-select__option-price-was');
          const compareValueEl = compareEl?.querySelector('.js-value');
          if (compareEl && compareValueEl) {
            // Written into .js-value, not the span itself: the sibling .js-label
            // carries the visually-hidden "regular price" wording that tells a
            // screen reader which of the two amounts is the struck one, and
            // replacing the whole span would delete it on the first change.
            //
            // Same innerHTML justification as the price above. `compare` is only
            // present on variants that carry a before-price, so the falsy branch
            // covers both "no variant for this combination" and "not discounted".
            compareValueEl.innerHTML = data && data.compare ? data.compare : '';
            compareEl.hidden = !(data && data.compare);
          }

          const stockEl = row.querySelector('.custom-select__option-stock');
          const stockTextEl = row.querySelector('.custom-select__option-stock-text');
          if (stockEl && stockTextEl) {
            // No variant for this value combined with the other rows' current
            // selection — the combination simply doesn't exist.
            const code = data ? data.stock : 'non_existent';
            stockEl.dataset.stock = code === 'non_existent' ? 'none' : code;
            stockTextEl.textContent = this.variantRows.labels[code] || '';
          }
        });
      });
    }

    /**
     * Finds the variant a rich row should price: this row's value combined with
     * whatever the other rows currently have selected. Rows with nothing
     * selected yet are ignored rather than treated as a non-match, so the first
     * paint of a pre-selection product still resolves a representative variant.
     * @param {number} optionIndex - Index of the option this row belongs to.
     * @param {string} value - The row's option value.
     * @returns {?object}
     */
    findVariantForRow(optionIndex, value) {
      if (!this.allVariants) return null;

      const selected = this.getSelectedOptions();
      selected[optionIndex] = value;

      return this.allVariants.find((variant) => variant.options.every(
        (optionValue, index) => selected[index] === null || optionValue === selected[index]
      )) || null;
    }

    /**
     * Updates the availability status in option selectors. When dynamic
     * availability is on, also auto-corrects any option whose checked value
     * just became unavailable in light of the new selection (e.g. picking a
     * color the currently-selected leg style doesn't come in) to the first
     * still-available value for that option, instead of leaving the
     * combination stuck on an invalid pair.
     *
     * data-availability is "true"/"false" (block.settings.enable_dynamic_availability
     * is a checkbox) — this used to check for the literal string 'prune',
     * a value the setting has never actually produced, which left this
     * auto-correct entirely dead since day one.
     */
    updateAvailability() {
      if (this.dataset.availability === 'true') {
        const toChange = this.querySelectorAll('.js-option.is-unavailable:is(:checked, [aria-selected="true"])');
        toChange.forEach((selected) => {
          const toSelect = selected.closest('.option-selector').querySelector('.js-option:not(.is-unavailable, [data-value=""])');
          if (toSelect) {
            setTimeout(() => {
              if (toSelect.closest('custom-select')) {
                toSelect.closest('custom-select').selectOption(toSelect);
              } else {
                toSelect.click();
              }
            }, 20); // Trigger after any updateContent logic has occurred
          }
        });
      }
    }

    /**
     * Updates the "Add to Cart" button label and disabled state.
     */
    updateAddToCartButton() {
      const productForm = this.getProductForm();
      if (!productForm) return;

      this.addBtn = productForm.querySelector('[name="add"]');

      // Zero price = missing catalog data, not purchasable. The ATC row hides
      // itself on the server render (it re-renders per variant), but the
      // dynamic-checkout wrapper is never replaced (payment-button hydration),
      // so its visibility must be kept in sync here.
      const zeroPrice = !!this.variant && this.variant.price === 0;
      const checkoutBtn = productForm.querySelector('.product-info__dynamic-checkout-button');
      if (checkoutBtn) checkoutBtn.hidden = zeroPrice;
      if (zeroPrice) {
        this.addBtn.disabled = true;
        return;
      }

      // Product not available
      if (!this.productAvailable) {
        this.addBtn.disabled = true;
        this.addBtn.textContent = theme.strings.noStock;
        return;
      }

      // Preselection
      if (this.getSelectedOptions().indexOf(null) !== -1) {
        if (this.addBtn.dataset.preselectionDisabled === 'true') {
          this.addBtn.disabled = true;
          this.addBtn.textContent = this.addBtn.dataset.preselectionText;
        } else {
          this.addBtn.disabled = false;
          this.addBtn.textContent = this.addBtn.dataset.addToCartText;
        }
        return;
      }

      // No variant
      if (!this.variant) {
        this.addBtn.disabled = true;
        this.addBtn.textContent = theme.strings.noVariant;
        return;
      }

      // Sold out
      if (!this.variant.available) {
        this.addBtn.disabled = true;
        this.addBtn.textContent = theme.strings.noStock;
        return;
      }

      // Add to cart
      this.addBtn.disabled = false;
      this.addBtn.textContent = this.addBtn.dataset.addToCartText;
    }

    /**
     * Updates the url with the selected variant id.
     */
    updateUrl() {
      if (this.dataset.updateUrl === 'false') return;
      const url = this.variant ? `${this.dataset.url}?variant=${this.variant.id}` : this.dataset.url;
      window.history.replaceState({ }, '', url);
    }

    /**
     * Gets the variant data for a product.
     * @returns {?object}
     */
    getVariantData() {
      const dataEl = this.section.querySelector('variant-picker [data-current-variant]');
      return dataEl ? JSON.parse(dataEl.textContent) : null;
    }

    /**
     * Gets the slim id/options/featured_media/available map for all of
     * this product's own variants (see variant-picker.liquid).
     * @returns {?Array}
     */
    getAllVariantsData() {
      const dataEl = this.section.querySelector('variant-picker [data-all-variants]');
      return dataEl ? JSON.parse(dataEl.textContent) : null;
    }

    /**
     * Gets the price/stock map and the translated stock wording for the rich
     * dropdown rows. Only present on a product where the long-option rule
     * actually converted a row (see variant-picker.liquid), so null is the
     * normal case and every consumer must tolerate it.
     * @returns {?object}
     */
    getVariantRowsData() {
      const dataEl = this.section.querySelector('variant-picker [data-variant-rows]');
      return dataEl ? JSON.parse(dataEl.textContent) : null;
    }

    /**
     * Finds this product's own variant matching a set of option values,
     * using the slim map above instead of a network request. Returns null
     * for a partial selection or a combined-listing swatch (a different
     * product, absent from this product's own variant map) — those cases
     * already fall back to waiting on the section-render fetch.
     * @param {Array} selectedOptions - Currently selected option values.
     * @returns {?object}
     */
    findVariantByOptions(selectedOptions) {
      if (!this.allVariants || selectedOptions.includes(null)) return null;
      return this.allVariants.find((v) => v.options.every(
        (value, index) => value === selectedOptions[index]
      )) || null;
    }

    /**
     * If the option value evt.target just selected doesn't combine with
     * the other currently-selected option(s) into an available variant,
     * re-selects those other option(s) to the first available alternative
     * for the value just picked — via click()/selectOption(), the same
     * path a shopper's own click takes, so every visual (button text,
     * swatch, aria-selected) stays correct.
     * @param {Element} changedTarget - evt.target from the 'change' event.
     * @returns {?object} - The corrected, available variant if a
     *   correction was made, else null (nothing to correct, or no
     *   available combination exists for the value just picked at all).
     */
    correctIncompatibleOptions(changedTarget) {
      if (!this.allVariants) return null;

      const selector = changedTarget.closest('.option-selector');
      const changedIndex = selector ? Array.from(this.optionSelectors).indexOf(selector) : -1;
      if (changedIndex === -1) return null;

      const selectedOptions = this.getSelectedOptions();
      if (selectedOptions.includes(null)) return null; // preselection still in progress

      const currentMatch = this.findVariantByOptions(selectedOptions);
      if (currentMatch && currentMatch.available) return null; // already a valid, available combination

      const changedValue = selectedOptions[changedIndex];
      const alternative = this.allVariants.find((v) => v.options[changedIndex] === changedValue && v.available);
      if (!alternative) return null; // nothing available combines with this value at all

      this.correctingOptions = true;
      try {
        alternative.options.forEach((value, index) => {
          if (index !== changedIndex && value !== selectedOptions[index]) {
            this.selectOptionValue(this.optionSelectors[index], value);
          }
        });
      } finally {
        this.correctingOptions = false;
      }

      return alternative;
    }

    /**
     * Selects a specific value within one option-selector (radio-input or
     * custom-select), the same way a shopper's own click would.
     * @param {Element} selector - An .option-selector element.
     * @param {string} value - The option value to select.
     */
    selectOptionValue(selector, value) {
      if (selector.dataset.selectorType === 'dropdown') {
        const option = Array.from(selector.querySelectorAll('.custom-select__option'))
          .find((el) => el.dataset.value === value);
        option?.closest('custom-select')?.selectOption(option);
        return;
      }
      Array.from(selector.querySelectorAll('.js-option'))
        .find((el) => el.value === value)
        ?.click();
    }

    /**
     * Get the selected values from a list of variant options.
     * @returns {Array} Array of selected option values, value is null if not selected.
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selected = selector.querySelector('.custom-select__option[aria-selected="true"]');
          selectedOptions.push(selected && selected.dataset.value !== '' ? selected.dataset.value : null);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptions.push(selected ? selected.value : null);
        }
      });

      return selectedOptions;
    }

    /**
     * Get the selected option value ids from a list of variant options.
     * @returns {Array} Array of selected option value ids, value is null if not selected.
     */
    getSelectedOptionIds() {
      const selectedOptionIds = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selected = selector.querySelector('.custom-select__option[aria-selected="true"]');
          selectedOptionIds.push(selected && selected.dataset.value !== '' ? selected.dataset.valueId : null);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptionIds.push(selected ? selected.dataset.valueId : null);
        }
      });

      return selectedOptionIds;
    }

    /**
     * Apply search parameters (set by product card swatches).
     */
    applySearchParams() {
      const searchParams = new URLSearchParams(window.location.search);

      Array.from(searchParams.keys()).forEach((key) => {
        const value = searchParams.get(key);
        const optionSelectors = Array.from(this.optionSelectors);
        const matchingOptionSelector = optionSelectors.find((x) => x.dataset.option === key);
        if (!matchingOptionSelector) return;

        if (matchingOptionSelector.dataset.selectorType === 'dropdown') {
          const colorOptionDropdown = matchingOptionSelector.querySelector(`.custom-select__option[data-value="${CSS.escape(value)}"]:not([aria-selected="true"])`);
          if (colorOptionDropdown) {
            const customSelect = colorOptionDropdown.closest('custom-select');
            const convertEvent = (evt) => {
              evt.detail.noFocus = true;
              customSelect.dispatchEvent(
                new CustomEvent('change', { bubbles: true, cancelable: false, detail: evt.detail })
              );
            };
            customSelect.addEventListener('change', convertEvent, { once: true });
            customSelect.selectOption(colorOptionDropdown);
          }
        } else {
          const matchingInput = matchingOptionSelector.querySelector(`input[value="${CSS.escape(value)}"]:not(:checked)`);
          if (matchingInput) {
            const matchingOptionValue = matchingOptionSelector.querySelector('.option-selector__label-value');
            if (matchingOptionValue) {
              matchingOptionSelector.querySelector('.option-selector__label-value').textContent = value;
            }
            matchingInput.checked = true;
            matchingInput.dispatchEvent(
              new CustomEvent('change', { bubbles: true, cancelable: false, detail: { noFocus: true } })
            );
          }
        }
      });
    }
  }

  customElements.define('variant-picker', VariantPicker);
}
